import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import {
  collection, getDocs, query, orderBy, doc, deleteDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, IconButton,
  Chip, CircularProgress, Avatar, Tooltip, TextField,
  InputAdornment, MenuItem, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import { deptCode } from '../utils/depts';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import CelebrationIcon from '@mui/icons-material/Celebration';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const TYPE_META = {
  pling: {
    label: 'PLING!',
    icon: NotificationsActiveIcon,
    color: '#FF6B00',
    bg: '#FFF3E0',
    border: '#FF6B00',
    emoji: '🔔',
  },
  'kling-klang': {
    label: 'KLING-KLANG!',
    icon: CelebrationIcon,
    color: '#1B5E20',
    bg: '#E8F5E9',
    border: '#2E7D32',
    emoji: '🏆',
  },
};

function formatDate(val) {
  if (!val) return '';
  if (val?.toDate) return val.toDate().toLocaleString('sv-SE');
  if (typeof val === 'string') return val;
  return '';
}

function formatSEK(val) {
  if (!val) return null;
  return Number(val).toLocaleString('sv-SE') + ' kr';
}

export default function PlingKlang() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [snap, deptSnap, userSnap] = await Promise.all([
      getDocs(query(collection(db, 'plingKlang'), orderBy('createdAt', 'desc'))),
      getDocs(collection(db, 'departments')),
      getDocs(collection(db, 'users')),
    ]);
    setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id) {
    if (!window.confirm('Ta bort meddelandet?')) return;
    await deleteDoc(doc(db, 'plingKlang', id));
    setEntries(prev => prev.filter(e => e.id !== id));
  }

  function handleCopy(entry) {
    navigator.clipboard.writeText(entry.aiText || entry.fallbackText || '');
    setCopiedId(entry.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  const filtered = entries.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      e.leadTitle?.toLowerCase().includes(q) ||
      e.companyName?.toLowerCase().includes(q);
    const matchType = !filterType || e.type === filterType;
    return matchSearch && matchType;
  });

  const plingCount = entries.filter(e => e.type === 'pling').length;
  const kkCount = entries.filter(e => e.type === 'kling-klang').length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              PLING & KLING-KLANG
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Interna nyheter om leads och vunna affärer
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#FF6B00', lineHeight: 1 }}>
              {plingCount}
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>PLING</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#4CAF50', lineHeight: 1 }}>
              {kkCount}
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>KLING-KLANG</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField size="small" placeholder="Sök lead eller företag..."
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            )}} />
          <TextField select size="small" value={filterType}
            onChange={e => setFilterType(e.target.value)} sx={{ minWidth: 180 }} label="Typ">
            <MenuItem value="">Alla</MenuItem>
            <MenuItem value="pling">PLING!</MenuItem>
            <MenuItem value="kling-klang">KLING-KLANG!</MenuItem>
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <NotificationsActiveIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {entries.length === 0
                ? 'Inga meddelanden ännu. Skapa ett PLING från ett lead!'
                : 'Inga träffar.'}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.map(entry => {
              const meta = TYPE_META[entry.type] || TYPE_META.pling;
              const Icon = meta.icon;
              const depts = departments.filter(d =>
                (entry.departmentIds || []).includes(d.id)
              );
              const creator = users.find(u => u.id === entry.createdBy);
              const isKK = entry.type === 'kling-klang';
              return (
                <Paper key={entry.id} sx={{
                  border: `2px solid ${meta.border}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}>
                  {/* Colored top bar */}
                  <Box sx={{
                    bgcolor: meta.color, px: 2.5, py: 1,
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '1.2rem' }}>{meta.emoji}</Typography>
                      <Typography fontWeight="bold" sx={{ color: 'white', fontSize: '1rem' }}>
                        {meta.label}
                      </Typography>
                      {isKK && (
                        <EmojiEventsIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 18 }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {formatDate(entry.createdAt)}
                      </Typography>
                      <Tooltip title={copiedId === entry.id ? 'Kopierat!' : 'Kopiera text'}>
                        <IconButton size="small"
                          onClick={() => handleCopy(entry)}
                          sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ta bort">
                        <IconButton size="small"
                          onClick={() => handleDelete(entry.id)}
                          sx={{ color: 'white', opacity: 0.5, '&:hover': { opacity: 1 } }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Body */}
                  <Box sx={{ p: 2.5, bgcolor: meta.bg }}>
                    {/* Meta row */}
                    <Box sx={{ display: 'flex', alignItems: 'center',
                      gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Avatar sx={{ width: 24, height: 24,
                          bgcolor: meta.color, fontSize: '0.65rem' }}>
                          <Icon sx={{ fontSize: 14 }} />
                        </Avatar>
                        <Typography variant="body2" fontWeight={700}>
                          {entry.leadTitle}
                        </Typography>
                      </Box>
                      {entry.companyName && (
                        <Chip label={entry.companyName} size="small"
                          sx={{ fontSize: '0.62rem', height: 18,
                            bgcolor: '#1a1a1a', color: 'white' }} />
                      )}
                      {entry.estimatedValue && (
                        <Chip label={formatSEK(entry.estimatedValue)} size="small"
                          sx={{ fontSize: '0.62rem', height: 18,
                            bgcolor: meta.color + '33', color: meta.color,
                            fontWeight: 700 }} />
                      )}
                      {depts.map(d => (
                        <Tooltip key={d.id} title={d.name}>
                          <Chip label={deptCode(d)} size="small"
                            sx={{ fontSize: '0.62rem', height: 18 }} />
                        </Tooltip>
                      ))}
                    </Box>

                    {/* AI text */}
                    <Typography variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {entry.aiText || entry.fallbackText}
                    </Typography>

                    {/* Creator */}
                    {creator && (
                      <Typography variant="caption" color="text.disabled"
                        sx={{ display: 'block', mt: 1.5 }}>
                        Skapat av {creator.displayName || creator.email}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
}
